'use client'

import React, { useRef, useEffect, useState } from "react";
import { LanguageType, LanguageDirection } from "@/lib/types";
import PrimaryButton from "@/components/ui/PrimaryButton/PrimaryButton";
import CompactButton from "@/components/ui/CompactButton/CompactButton";
import PlayerButton from "@/components/ui/PlayerButton/PlayerButton";
import Subheading from "@/components/ui/Subheading/Subheading";
import { CaretLeft, CaretRight, Play, Pause, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel, Pagination, Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper/types";
import '@/components/ui/TestimonialCard/TestimonialCard.css';
import Image from "next/image";
import MuxPlayer from "@mux/mux-player-react";
import { useIsLg } from '@/contexts/BreakpointContext';

// Hardcoded placeholder media until each card gets its own Mux player.
const PLACEHOLDER_IMAGE = "/3_Landing-Page/Landing-1-Hero/Landing-Hero.webp";

interface ValueType {
  heading: string
  paragraph: string
  author?: string
  muxPlaybackId?: string
}

interface UGCBenefitsSectionProps {
  language?: LanguageType
  subheadingText: string
  values: ValueType[]
  buttonText?: string
  testimonial?: {
    quote: string
    author: string
  }
  onOpenSlidingForm?: () => void
  id?: string
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

const UGCBenefitsSection: React.FC<UGCBenefitsSectionProps> = ({
  language = "english",
  subheadingText,
  values,
  buttonText = "Book your free consultation",
  testimonial,
  onOpenSlidingForm,
  id,
}) => {
  const isLg = useIsLg();
  const swiperRef = useRef<SwiperType | null>(null);
  // Players are mounted up-front (one ref per card) so they start preloading immediately.
  const playerRefs = useRef<Record<number, any>>({});
  // Only one card plays at a time. null = nothing playing.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  // Whether the Pause control is currently revealed over the playing video.
  const [controlsVisible, setControlsVisible] = useState(false);
  // Per-card mute state. Streams start with sound on (play is user-initiated).
  const [mutedByIndex, setMutedByIndex] = useState<Record<number, boolean>>({});

  const pauseAll = () => {
    Object.values(playerRefs.current).forEach((p) => p?.pause());
  };

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.snapGrid = [...swiperRef.current.slidesGrid];
    }
  }, []);

  const handlePrev = () => swiperRef.current?.slidePrev();
  const handleNext = () => swiperRef.current?.slideNext();

  const cardClassName = `
    relative
    rounded-[24px] lg:rounded-[32px]
    outline outline-2 outline-theme-yellow-primary-base
    bg-theme-yellow-primary-dark
    overflow-hidden
    shadow-sunshine-primary-glow
  `;

  const renderMedia = (value: ValueType, index: number) => {
    const isActive = activeIndex === index;
    const isMuted = mutedByIndex[index] ?? false;
    const posterUrl = value.muxPlaybackId
      ? `https://image.mux.com/${value.muxPlaybackId}/thumbnail.jpg?time=0.5`
      : PLACEHOLDER_IMAGE;

    return (
      <div className="relative z-10 w-full aspect-[9/16] -mt-[1.5rem] lg:-mt-[2rem] overflow-hidden rounded-[24px] lg:rounded-[32px] outline outline-2 outline-theme-yellow-primary-base">
        {value.muxPlaybackId && (
          <MuxPlayer
            ref={(el) => { playerRefs.current[index] = el; }}
            streamType="on-demand"
            playbackId={value.muxPlaybackId}
            poster={posterUrl}
            preload="auto"
            loop
            playsInline
            muted={isMuted}
            className="w-full h-full"
            style={{
              "--controls": "none",
              "--media-object-fit": "cover",
              "--media-object-position": "center center",
            } as any}
          />
        )}
        {value.muxPlaybackId && (
          <div
            className={`absolute bottom-[1rem] right-[1rem] z-20 transition-opacity duration-300 ease-[ease] ${isActive ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
          >
            <PlayerButton
              size={44}
              aria-label={isMuted ? "Unmute" : "Mute"}
              onClick={() => setMutedByIndex((prev) => ({ ...prev, [index]: !isMuted }))}
            >
              {isMuted
                ? <SpeakerSlash className="text-graphics-reversed" size={20} />
                : <SpeakerHigh className="text-graphics-reversed" size={20} />}
            </PlayerButton>
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          {!isActive ? (
            <PlayerButton
              size={68}
              onClick={() => {
                pauseAll();
                playerRefs.current[index]?.play();
                setActiveIndex(index);
                setControlsVisible(false);
              }}
            >
              <Play className="text-graphics-reversed" size={24} />
            </PlayerButton>
          ) : (
            <>
              {/* Tap/click the playing video to reveal the Pause control */}
              <button
                type="button"
                aria-label="Show controls"
                className="absolute inset-0 cursor-pointer"
                onClick={() => setControlsVisible((v) => !v)}
              />
              <div
                className={`transition-opacity duration-300 ease-[ease] ${controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
                  }`}
              >
                <PlayerButton
                  size={68}
                  onClick={() => {
                    playerRefs.current[index]?.pause();
                    setActiveIndex(null);
                    setControlsVisible(false);
                  }}
                >
                  <Pause className="text-graphics-reversed" size={24} />
                </PlayerButton>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCard = (value: ValueType, index: number) => (
    <>
      <p className="
        text-text-reversed text-20-to-18--alexandria text-center
        pt-[1.75rem] pb-[2.5rem] px-[1.75rem]
        lg:pt-[2rem] pb-[3.5rem] lg:px-[2rem]
      ">
        {value.heading}
      </p>
      {renderMedia(value, index)}
      <div className="
        relative
        -mt-[1.5rem] lg:-mt-[2rem]
        rounded-b-[24px] lg:rounded-b-[32px]
        outline outline-2 outline-theme-yellow-primary-base
        bg-bg-light-primary
        overflow-hidden
        px-[1.75rem] pt-[2.5rem] pb-[1rem]
        lg:px-[2rem] lg:pt-[3.5rem] lg:pb-[1.25rem]
        flex flex-col gap-[1rem]
      ">
        <p className="text-text-primary text-18--alexandria text-center">
          {value.paragraph}
        </p>
        <p className="text-text-primary text-18--alexandria text-center">
          {value.author}
        </p>
      </div>
    </>
  );

  return (
    <section
      id={id}
      style={{ direction: LANGUAGE_TO_DIRECTION[language], overscrollBehaviorX: 'none' }}
      className="
      w-full
      overflow-hidden
      flex flex-col items-center justify-center
      gap-y-[2.5rem] lg:gap-y-[4rem]
      px-[0.75rem] tb:px-[3.5rem] lg:px-[3.5rem]
      "
    >
      <div className="w-full container-x-small relative z-10">
        <div className="w-full flex flex-col items-center justify-center gap-y-[1.5rem] lg:gap-y-[3.5rem]">
          <h2 className="text-text-primary text-52-to-28--neue-june text-center">
            {subheadingText}
          </h2>
        </div>
      </div>

      <div className="w-full container-large">
        {isLg ? (
          <div className="grid grid-cols-3 items-start gap-x-[2rem] gap-y-[2.5rem]">
            {values.map((value, index) => (
              <div key={index} className={cardClassName}>
                {renderCard(value, index)}
              </div>
            ))}
          </div>
        ) : (
          <Swiper
            style={{ width: '100%', overflow: 'visible' }}
            spaceBetween={32}
            breakpoints={{ 768: { spaceBetween: 68 } }}
            slidesPerView={1}
            loop={false}
            modules={[Pagination, Autoplay, Mousewheel]}
            pagination={{
              clickable: true,
              el: '.swiper-custom-pagination',
              bulletClass: 'swiper-pagination-bullet',
              bulletActiveClass: 'swiper-pagination-bullet-active',
            }}
            onBeforeInit={(swiper) => { swiperRef.current = swiper; }}
            onSlideChange={() => { pauseAll(); setActiveIndex(null); setControlsVisible(false); }}
            className="w-full !overflow-visible testimonial-swiper"
          >
            {values.map((value, index) => (
              <SwiperSlide key={index} className="cursor-grab">
                <div className={cardClassName}>
                  {renderCard(value, index)}
                </div>
              </SwiperSlide>
            ))}

            {values.length > 1 && (
              <div
                style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                className="mt-[1.5rem] tb:mt-[2rem] w-full flex items-center justify-between"
              >
                <CompactButton size={44} radius={9999} onClick={handlePrev}>
                  {language === "english" ? <CaretLeft /> : <CaretRight />}
                </CompactButton>
                <div className="swiper-custom-pagination flex justify-center gap-[0.625rem]" />
                <CompactButton size={44} radius={9999} onClick={handleNext}>
                  {language === "english" ? <CaretRight /> : <CaretLeft />}
                </CompactButton>
              </div>
            )}
          </Swiper>
        )}
      </div>

      <div
        style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
        className="w-full container-large overflow-visible flex flex-col items-center justify-center gap-[1.5rem] lg:gap-[2rem]"
      >
        <div style={{ direction: "ltr" }} className="w-fit">
          <PrimaryButton
            text={buttonText}
            onClick={() => { if (onOpenSlidingForm) onOpenSlidingForm(); }}
            size={isLg ? 64 : 52}
            type="accent"
            language={language}
            icon={true}
            isLPCtaButton={true}
          />
        </div>
        {testimonial && (
          <div
            style={{ direction: "ltr" }}
            className="w-full text-center flex flex-col items-center justify-center gap-[1rem] lg:gap-[1.5rem]"
          >
            <p className="text-text-quaternary text-20--alexandria">
              {testimonial.quote}
            </p>
            <Subheading
              size={isLg ? 20 : 16}
              subheading={testimonial.author}
              alignment="center"
              textColor="text-text-quaternary"
            />
          </div>
        )}
      </div>
    </section>
  );
}

export default UGCBenefitsSection;
